import { RiEyeOffLine, RiFilter3Line, RiPushpin2Line } from "@remixicon/react";
import { Badge } from "@screener/uikit/components/badge";
import { buttonVariants } from "@screener/uikit/components/button";
import { Card, CardContent } from "@screener/uikit/components/card";
import { cn } from "@screener/uikit/lib/utils";
import { useEffect, useState } from "react";
import {
  CARD_BODY_COLUMNS_CLASS,
  CARD_HEADER_CLASS,
  CARD_ROW_SHELL_CLASS,
  CARD_STRIP_CLASSES,
} from "@/density/card-classes";
import { CARD_COLUMN_GAP_PX, CARD_COLUMN_MIN_WIDTH_PX } from "@/density/cards";
import { ExchangeLogo } from "@/density/exchange-logo";
import { formatAge } from "@/density/format";
import { marketLabel } from "@/lib/labels";
import {
  type AllSlots,
  type CardSlots,
  createSlots,
  DEMO_CARDS,
  DEMO_COLUMNS,
  type DemoCard,
  type DemoColumnParams,
  formatDemoUsd,
  formatDistancePct,
  lastActiveIndex,
  maxActiveUsd,
  rowMarginsPx,
  SLOT_OFFSETS_PCT,
  slotPrice,
  slotSide,
  splitDemoColumns,
  startCardsTicker,
  tickSlots,
} from "./decorative-cards-model";

/**
 * Колонки карточек: параметры колонки и её монеты. Число колонок задаёт
 * только `DEMO_COLUMNS`, монеты раскладываются под него.
 */
const COLUMN_CARDS = splitDemoColumns(DEMO_CARDS, DEMO_COLUMNS.length);
const COLUMNS = DEMO_COLUMNS.map((params, index) => ({
  cards: COLUMN_CARDS[index] ?? [],
  params,
}));

/**
 * Декоративная имитация экрана карточек для правой колонки входа
 * (stage9-auth-redesign, Д5). Данные фиктивные, к потоку не подключается;
 * для чтения с экрана и указателя недоступна. Колонки независимы: свой набор
 * монет, свой тикер и вертикальный сдвиг; вторая колонка видна с xl. При
 * «уменьшить движение» таймеров нет, кадр неподвижен.
 */
export function DecorativeDensityCards({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      data-slot="decorative-density-cards"
      className={cn(
        "pointer-events-none flex min-h-0 justify-start mask-[linear-gradient(to_bottom,transparent_0%,#000_16%,#000_84%,transparent_100%)]",
        className,
      )}
      style={{ columnGap: CARD_COLUMN_GAP_PX }}
    >
      {COLUMNS.map(({ cards, params }, index) => (
        <DecorativeColumn
          key={cards[0]?.ticker ?? index}
          cards={cards}
          params={params}
          className={index === 0 ? "flex" : "hidden xl:flex"}
        />
      ))}
    </div>
  );
}

function DecorativeColumn({
  cards,
  params,
  className,
}: {
  cards: readonly DemoCard[];
  params: DemoColumnParams;
  className: string;
}) {
  const [slots, setSlots] = useState<AllSlots>(() => createSlots(cards, Math.random));

  useEffect(
    () =>
      startCardsTicker(() => setSlots((current) => tickSlots(current, Math.random)), {
        query: params.query,
        intervalMs: params.intervalMs,
      }),
    [params.query, params.intervalMs],
  );

  return (
    <div
      data-slot="decorative-density-column"
      className={cn("w-[340px] max-w-full flex-col justify-center gap-3", className)}
      style={{
        minWidth: CARD_COLUMN_MIN_WIDTH_PX,
        transform: `translateY(${params.offsetPx}px)`,
      }}
    >
      {cards.map((card, index) => (
        <DecorativeCard key={card.ticker} card={card} slots={slots[index] ?? []} />
      ))}
    </div>
  );
}

/**
 * Значок шапки: те же классы, что у кнопок шапки настоящей карточки
 * (`ghost`, `icon-cell`), но на `span`, не кнопка.
 */
const HEADER_ICON_CLASS = buttonVariants({ variant: "ghost", size: "icon-cell" });

function DecorativeCard({ card, slots }: { card: DemoCard; slots: CardSlots }) {
  const max = maxActiveUsd(slots);
  const lastOnIndex = lastActiveIndex(slots);
  const margins = rowMarginsPx(slots);
  return (
    <Card variant="lift" size="sm" className="w-full max-w-[340px] flex-none">
      <div className={CARD_HEADER_CLASS}>
        <ExchangeLogo exchange={card.exchange} className="h-3.5" />
        <span className="text-[14px] font-semibold tracking-[-0.005em] whitespace-nowrap">
          {card.ticker}
        </span>
        <Badge variant="outline-muted" size="tag">
          {marketLabel(card.market)}
        </Badge>
        <span className="flex-1" />
        <div className="flex items-center text-muted-foreground">
          <span className={HEADER_ICON_CLASS}>
            <RiFilter3Line aria-hidden="true" />
          </span>
          <span className={HEADER_ICON_CLASS}>
            <RiPushpin2Line aria-hidden="true" />
          </span>
          <span className={HEADER_ICON_CLASS}>
            <RiEyeOffLine aria-hidden="true" />
          </span>
        </div>
      </div>
      {/* Без `gap-y`: скрытая строка высотой 0 оставляла бы свой зазор 2 px,
          поэтому ритм ведут отступы строк (`rowMarginsPx`). */}
      <CardContent className={CARD_BODY_COLUMNS_CLASS}>
        {SLOT_OFFSETS_PCT.map((offset, index) => {
          const slot = slots[index];
          if (slot === undefined) {
            return null;
          }
          const strip = CARD_STRIP_CLASSES[slotSide(index)];
          return (
            <div
              key={offset}
              className={cn(
                CARD_ROW_SHELL_CLASS,
                strip.track,
                // Скрытая строка из DOM не убирается: нужна анимация.
                "[transition:height_480ms_cubic-bezier(.2,0,.2,1),margin-top_480ms_cubic-bezier(.2,0,.2,1),opacity_480ms_linear] motion-reduce:[transition:none]",
                slot.on ? "opacity-100" : "h-0 opacity-0",
                margins[index] === 0 ? "mt-0" : "mt-0.5",
                // `last:` попадает и на скрытую последнюю строку, поэтому
                // скругление низа ставится на последнюю включённую.
                index === lastOnIndex && "rounded-b-[6px]",
              )}
            >
              <span
                className={cn(
                  "absolute inset-y-0 right-0 [transition:width_600ms_cubic-bezier(.2,0,.2,1)] motion-reduce:[transition:none]",
                  strip.fill,
                )}
                style={{ width: slot.on ? `${((slot.usd / max) * 100).toFixed(1)}%` : "0%" }}
              />
              <span className={cn("absolute inset-0 rounded-[inherit] border-r-2", strip.edge)} />
              <span className="relative font-semibold whitespace-nowrap">
                {slotPrice(card, offset)}
              </span>
              <span className="relative text-right whitespace-nowrap text-muted-foreground">
                {formatAge(slot.age)}
              </span>
              <span className="relative text-right whitespace-nowrap font-[450] text-muted-foreground">
                {formatDistancePct(offset)}
              </span>
              <span className="relative text-right font-semibold whitespace-nowrap text-foreground">
                {formatDemoUsd(slot.usd)}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
